import { useEffect, useState } from "react";
import axios from "axios";
import isEmpty from "validator/lib/isEmpty";
import { useDebounce } from "@uidotdev/usehooks";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";

export default () => {
  const [searchResults, setSearchResults] = useState();
  const [pendingSearch, setPendingSearch] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { id: stateUserId } = useUserStore((state) => state.user, shallow);

  async function algoliaClient() {
    const { algoliasearch } = await import("algoliasearch");
    const algoliaConfig = {
      appId: import.meta.env.VITE_ALGOLIA_APP_ID,
      apiKey: import.meta.env.VITE_ALGOLIA_API_KEY,
    };

    return algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey);
  }

  const isDev = !!import.meta.env.DEV;
  // const isDev = false;

  const INDEX_PREFIX = isDev ? "dev_" : "";

  // const INDEXING_API_URL = isDev
  //   ? `http://localhost:3000`
  //   : `http://34.72.164.198:3000`;

  const debouncedQuery = useDebounce(pendingSearch?.query, 500);

  /**
   * This is the main search function, and search results are returned, or can be retrieved from the state variable `searchResults`
   * @param {Object} props
   * @param {'global' | 'users' | 'posts'} props.type
   * @param {string} props.query
   * @param {Number} props.minQueryLength
   * @param {Number} props.limit The number of search results to return
   * @param {string[]} props.excludeIDs The number of search results to return
   * @returns {Promise<{type: string, hits: []}[] | []>}
   */
  async function executeSearch({
    type = "global",
    query = "",
    minQueryLength = 3,
    limit = 10,
    excludeIDs,
  }) {
    if (query.length < minQueryLength) {
      setSearchResults(null);
      return;
    }
    try {
      setIsPending(true);
      const getExcludeIDsFilter = () => {
        if (excludeIDs?.length > 0)
          return {
            filters: excludeIDs
              .map((id, index) =>
                index == 0 ? `NOT objectID:${id}` : `AND NOT objectID:${id}`
              )
              .join(" "),
          };
      };

      if (type === "global") {
        const client = await algoliaClient();
        const _searchResults = await client.search({
          requests: [
            {
              indexName: `${INDEX_PREFIX}collection_posts`,
              clickAnalytics: true,
              query,
              hitsPerPage: limit,
              userToken: stateUserId,
              ...getExcludeIDsFilter(),
            },
            {
              indexName: `${INDEX_PREFIX}collection_users`,
              clickAnalytics: true,
              query,
              hitsPerPage: limit,
              userToken: stateUserId,
              ...getExcludeIDsFilter(),
            },
          ],
        });

        if (_searchResults.results && Array.isArray(_searchResults.results)) {
          const mapped = _searchResults.results.map((result) => ({
            type: result.index?.split("_")?.pop(),
            hits: result.hits?.map((document) => ({
              ...document,
              queryId: result.queryID,
            })),
          }));
          setSearchResults(mapped);
          setIsPending(false);
          return mapped;
        }
      } else {
        const client = await algoliaClient();
        const _searchResults = await client.searchSingleIndex({
          indexName: `${INDEX_PREFIX}collection_${type}`,
          searchParams: {
            clickAnalytics: true,
            query,
            hitsPerPage: limit,
            userToken: stateUserId,
            ...getExcludeIDsFilter(),
          },
        });
        if (_searchResults.hits && Array.isArray(_searchResults?.hits)) {
          const mapped = _searchResults?.hits.map((document) => ({
            ...document,
            queryId: _searchResults.queryID,
          }));
          setSearchResults(mapped);
          setIsPending(false);
          return mapped;
        }
      }
    } catch (ex) {
      console.log("Could not perform seach!", ex);
      setIsPending(false);
    }
  }

  /**
   * This is the debounced search function, and search results can be retrieved from the state variable `searchResults`
   * @param {Object} props
   * @param {'global' | 'users' | 'posts'} props.type
   * @param {string} props.query
   * @param {Number} props.minQueryLength
   */
  function performSearch({ type = "global", query, minQueryLength = 3 }) {
    // Buffer incoming requests; real search will run after debounce delay
    if (isEmpty(query) || query.length < minQueryLength) {
      setSearchResults(null);
      setPendingSearch({ type, query, minQueryLength });
      return;
    }
    setPendingSearch({ type, query, minQueryLength });
  }

  function resetSearch() {
    setSearchResults(null);
  }

  useEffect(() => {
    if (!pendingSearch) return;
    // When debounced query updates, execute the search using latest params
    executeSearch({
      type: pendingSearch.type,
      query: debouncedQuery ?? "",
      minQueryLength: pendingSearch.minQueryLength,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  return {
    searchResults,
    performSearch,
    resetSearch,
    executeSearch,
    isPending,
  };
};
